import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteApiClient, fetchApiClientSecret, fetchMcpClients } from 'commons/src/actions/apiClientActions';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import InputLabel from 'commons/src/components/input/InputLabel';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import DeleteConfirmModal from 'commons/src/components/modals/DeleteConfirmModal';
import { RequiredRoleLevel } from 'commons/src/models/commonEnums';
import { ActionButton } from 'commons/src/models/menuModels';
import { Store } from 'commons/src/reducers';
import { CommonRequestType } from 'commons/src/reducers/requestReducer';
import { paths } from '../../../constants';

const McpClientDetail = (): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { clientId } = useParams<'clientId'>();

    const mcpClients = useSelector((store: Store) => store.apiClients.mcpClients);
    const clientSecret = useSelector((store: Store) => store.apiClients.clientSecret);
    const secretLoading = useSelector(
        (store: Store) => store.commonRequests[CommonRequestType.FetchApiClientSecret].loading
    );
    const loadingList = useSelector((store: Store) => store.commonRequests[CommonRequestType.FetchMcpClients].loading);
    const deleteLoading = useSelector(
        (store: Store) => store.commonRequests[CommonRequestType.DeleteApiClient].loading
    );
    const deleteError = useSelector((store: Store) => store.commonRequests[CommonRequestType.DeleteApiClient].error);

    const [displayDeleteModal, setDisplayDeleteModal] = useState(false);

    const client = clientId ? mcpClients.find(c => c.id === clientId) : undefined;

    useEffect(() => {
        if (mcpClients.length === 0 && !loadingList) {
            dispatch(fetchMcpClients());
        }
    }, [dispatch]);

    useEffect(() => {
        if (clientId && !loadingList && mcpClients.length > 0 && !client) {
            navigate(`/${paths.integrationsMcp}`);
        }
    }, [clientId, loadingList, mcpClients, client, navigate]);

    const close = (): void => navigate(`/${paths.integrationsMcp}`);

    const revealSecret = (): void => {
        if (client) {
            dispatch(fetchApiClientSecret(client.id));
        }
    };

    const onDeleteClick = (e: SyntheticEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        if (client) setDisplayDeleteModal(true);
    };

    const closeDeleteModal = (): void => setDisplayDeleteModal(false);

    const confirmDelete = (): void => {
        if (client) dispatch(deleteApiClient(client.id));
    };

    const actionButtons: ActionButton[] = [
        {
            onClick: close,
            testAttr: 'close-mcp-client-detail',
            id: 'CloseMcpClientDetail',
            title: 'Close',
            color: 'secondary',
            requiredGroupTypes: [],
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
        },
    ];

    const secretIsForThisClient = !!client && !!clientSecret && clientSecret.clientId === client.id;
    const redirectUris = client?.redirectUris ?? [];

    return (
        <>
            {displayDeleteModal && client && (
                <DeleteConfirmModal
                    title="ApiIntegration.DeleteClient"
                    description={txt('Webhooks.DeleteDescription', { name: client.name })}
                    onSubmit={confirmDelete}
                    onCancel={closeDeleteModal}
                    onSubmitText="Delete"
                    onCancelText="Cancel"
                    loading={deleteLoading}
                    error={!!(deleteError && deleteError.error)}
                    errorText="SomethingWentWrong"
                />
            )}
            <PageHeader title={txt('McpIntegration.ClientDetailTitle')} />
            {client && (
                <>
                    <div className="page-wrapper__medium">
                        <SubHeader actionButtons={actionButtons} />
                    </div>
                    <div className="page-wrapper__medium page-wrapper__medium--white">
                        <div className="settings-details-container">
                            <h2 className="settings__header settings__header--no-margin-top">{client.name}</h2>
                            {!!client.description && <p>{client.description}</p>}

                            <div className="form__row">
                                <div className="form__attr--element">
                                    <InputLabel htmlFor="mcp-detail-client-id" label="McpIntegration.ClientIdLabel" />
                                    <div className="form__attr" id="mcp-detail-client-id">
                                        {client.id}
                                    </div>
                                </div>
                            </div>

                            <div className="form__row">
                                <div className="form__attr--element">
                                    <InputLabel htmlFor="mcp-detail-scope" label="ApiIntegration.ResourceScope" />
                                    <div className="form__attr" id="mcp-detail-scope">
                                        {client.authScope.join(', ')}
                                    </div>
                                </div>
                            </div>

                            {redirectUris.length > 0 && (
                                <div className="form__row">
                                    <div className="form__attr--element">
                                        <InputLabel
                                            htmlFor="mcp-detail-redirect-uris"
                                            label="ApiIntegration.RedirectUri"
                                        />
                                        <div className="form__attr" id="mcp-detail-redirect-uris">
                                            {redirectUris.join(', ')}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form__row">
                                <div className="form__attr--element">
                                    <InputLabel htmlFor="mcp-detail-client-secret" label="ApiIntegration.Secret" />
                                    <div className="form__attr--element centered">
                                        {secretIsForThisClient && clientSecret ? (
                                            <div
                                                id="mcp-detail-client-secret"
                                                className="accounts-view__form__message accounts-view__form__message--left
                                                    accounts-view__form__message--break-word"
                                            >
                                                {clientSecret.secret}
                                            </div>
                                        ) : (
                                            <PrimaryButton
                                                color="secondary"
                                                onClick={revealSecret}
                                                type="button"
                                                title="McpIntegration.RevealSecret"
                                                loading={secretLoading}
                                                icon={<MaterialIcon name="remove_red_eye" />}
                                                testAttr="mcp-detail-reveal-secret"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="form__row form__button-container">
                                <div className="form__attr--element centered">
                                    <PrimaryButton
                                        onClick={onDeleteClick}
                                        type="button"
                                        title="Delete"
                                        color="alert"
                                        loading={deleteLoading}
                                        testAttr="delete-mcp-client"
                                        testId="delete-mcp-client"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default McpClientDetail;
